/**
 * StationTable
 *
 * Lets you filter/search stations and choose which ones
 * to show in the slideshow. You can also set a timer per station and pick
 * languages for each.
 *
 * summary:
 * - Controlled table using `rows` and `filteredRows`
 * - Filter controls for status, type, and community
 * - Inline controls for display toggle, timer, and languages
 * - Styled scrollbars and sticky headers for kiosk-sized screens
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.rows - Array of station data
 * @param {Array} props.filteredRows - Filtered station data for display
 * @param {Object} props.filters - Current filter state
 * @param {string} props.search - Current search term
 * @param {Function} props.onDisplayChange - Handler for display checkbox changes
 * @param {Function} props.onTimerChange - Handler for timer input changes
 * @param {Function} props.onSearchChange - Handler for search input changes
 * @param {Function} props.onFilterChange - Handler for filter changes
 * @param {Array} props.statusOptions - Available status filter options
 * @param {Array} props.typeOptions - Available type filter options
 * @param {Array} props.communityOptions - Available community filter options
 * @param {boolean} props.showMapButton - Whether to show map selection button
 * @param {Function} props.onMapButtonClick - Handler for map button click
 * @param {boolean} props.showSaveButton - Whether to show save button
 * @param {Function} props.onSaveClick - Handler for save button click
 * @param {Object} props.mapConfig - Current map configuration
 * @param {Function} props.onMapConfigChange - Handler for map configuration changes
 * @param {string} props.lastModified - Last modified timestamp
 * @param {Function} props.formatLastModified - Function to format last modified date
 */

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  Button,
  Tooltip,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  Autocomplete,
  TextField as MuiTextField,
  Chip,
  Grid,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SearchIcon from "@mui/icons-material/Search";
import MapIcon from "@mui/icons-material/Map";

import {
  TABLE_SCROLLBAR_WIDTH,
  TABLE_SCROLLBAR_COLORS,
} from "../utils/constants";
import {
  formatCoordinates,
  formatStationName,
  calculateMapArea,
} from "../utils/helpers";

// Mapping of internal type values to user-friendly display names
const TYPE_DISPLAY_NAMES = {
  WIRL_Weather_Station: "Community-led",
  ECCC_Weather_Station: "Government",
  NTI_Weather_Station: "Research",
  // Add more mappings as needed
};

const StationTable = ({
  rows,
  filteredRows,
  filters,
  search,
  onDisplayChange,
  onTimerChange,
  onLanguageChange,
  onSearchChange,
  onFilterChange,
  statusOptions,
  typeOptions,
  communityOptions,
  showMapButton = false,
  onMapButtonClick,
  showSaveButton = false,
  onSaveClick,
  mapConfig,
  onMapConfigChange,
  lastModified,
  formatLastModified,
}) => {
  /**
   * Handles autocomplete filter changes
   * @param {string} key - Filter key to update
   * @returns {Function} Event handler function
   */
  // Inline curried handler to update a single filter by key
  const handleAutoFilterChange = (key) => (event, value) => {
    onFilterChange({ ...filters, [key]: value || "" });
  };

  // Type Filter
  // Map typeOptions to display options
  // Map raw type values to human-friendly labels for the UI
  const typeDisplayOptions = typeOptions.map((type) => ({
    value: type,
    label: TYPE_DISPLAY_NAMES[type] || type,
  }));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: "100vh",
        maxHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        p: { xs: 1, sm: 3 },
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Search Bar */}
      <Box sx={{ mb: 2, width: { xs: "100%", sm: 400 } }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by station name, ID, or community..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filters and Action Buttons */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, alignItems: "center", justifyContent: "space-between" }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ flex: 1 }}
        >
          {/* Status Filter */}
          <Autocomplete
            options={statusOptions}
            value={filters.status}
            onChange={handleAutoFilterChange("status")}
            renderInput={(params) => (
              <MuiTextField {...params} label="Status" size="small" />
            )}
            sx={{ minWidth: 140 }}
            clearOnEscape
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* Type Filter */}
          <Autocomplete
            options={typeDisplayOptions}
            value={
              typeDisplayOptions.find((opt) => opt.value === filters.type) ||
              null
            }
            onChange={(event, newValue) => {
              onFilterChange({
                ...filters,
                type: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <MuiTextField {...params} label="Type" size="small" />
            )}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            sx={{ minWidth: 180 }}
            clearOnEscape
          />

          {/* Community Filter */}
          <Autocomplete
            options={communityOptions}
            value={filters.Community}
            onChange={handleAutoFilterChange("Community")}
            renderInput={(params) => (
              <MuiTextField {...params} label="Community" size="small" />
            )}
            sx={{ minWidth: 180 }}
            clearOnEscape
            isOptionEqualToValue={(option, value) => option === value}
          />
        </Stack>
      </Stack>

      {/* Station Table */}
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          borderRadius: 2,
          boxShadow: 3,
          overflowY: "scroll",
          "::-webkit-scrollbar": {
            width: TABLE_SCROLLBAR_WIDTH,
            background: TABLE_SCROLLBAR_COLORS.background,
          },
          "::-webkit-scrollbar-thumb": {
            background: TABLE_SCROLLBAR_COLORS.thumb,
            borderRadius: 8,
            border: `4px solid ${TABLE_SCROLLBAR_COLORS.background}`,
          },
          scrollbarWidth: "auto",
          scrollbarColor: `${TABLE_SCROLLBAR_COLORS.thumb} ${TABLE_SCROLLBAR_COLORS.background}`,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Display
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                <Tooltip
                  title="Set timer in seconds for display rotation"
                  arrow
                >
                  <span>Timer (sec)</span>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Languages
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Station (ID)
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Community
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Type
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Location
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Coordinates
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row, idx) => {
              const isSelected = row.display;
              const rowIndex = rows.findIndex((r) => r.id === row.id);

              return (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor:
                      idx % 2 === 0 ? "background.default" : "grey.100",
                    "&:hover": { backgroundColor: "action.hover" },
                    transition: "background 0.2s",
                    opacity: isSelected ? 1 : 0.5,
                  }}
                >
                  {/* Display Checkbox */}
                  <TableCell>
                    <Checkbox
                      checked={row.display}
                      onChange={onDisplayChange(rowIndex)}
                      color="primary"
                    />
                  </TableCell>

                  {/* Timer Input */}
                  <TableCell>
                    {row.display ? (
                      <TextField
                        size="small"
                        type="number"
                        value={row.timer}
                        onChange={onTimerChange(rowIndex)}
                        inputProps={{ min: 1, style: { width: 70 } }}
                        variant="outlined"
                      />
                    ) : row.timer ? (
                      row.timer
                    ) : (
                      ""
                    )}
                  </TableCell>

                  {/* Language Checkboxes */}
                  <TableCell>
                    {row.display ? (
                      <Stack direction="row" spacing={1}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Tooltip
                            title={
                              !row.languages?.inuktitut &&
                              row.languages?.english
                                ? "At least one language must be selected"
                                : "English"
                            }
                            arrow
                          >
                            <span>
                              <Checkbox
                                size="small"
                                checked={row.languages?.english || false}
                                onChange={onLanguageChange(rowIndex)("english")}
                                color="primary"
                                disabled={
                                  !row.languages?.inuktitut &&
                                  row.languages?.english
                                }
                              />
                            </span>
                          </Tooltip>
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            EN
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Tooltip
                            title={
                              !row.languages?.english &&
                              row.languages?.inuktitut
                                ? "At least one language must be selected"
                                : "Inuktitut"
                            }
                            arrow
                          >
                            <span>
                              <Checkbox
                                size="small"
                                checked={row.languages?.inuktitut || false}
                                onChange={onLanguageChange(rowIndex)(
                                  "inuktitut"
                                )}
                                color="primary"
                                disabled={
                                  !row.languages?.english &&
                                  row.languages?.inuktitut
                                }
                              />
                            </span>
                          </Tooltip>
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            IN
                          </Typography>
                        </Box>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  {/* Station Name and ID */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontStyle: isSelected ? "normal" : "italic",
                        }}
                        color={isSelected ? "text.primary" : "text.secondary"}
                      >
                        {formatStationName(row.name, row.id)}
                      </Typography>
                      {!isSelected && (
                        <Tooltip title="Not visible" arrow>
                          <IconButton
                            size="small"
                            sx={{ p: 0.5, color: "grey.500" }}
                          >
                            <VisibilityOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  {/* Community */}
                  <TableCell>{row.Community || "—"}</TableCell>

                  {/* Status */}
                  <TableCell>{row.status}</TableCell>

                  {/* Type */}
                  <TableCell>
                    {TYPE_DISPLAY_NAMES[row.type] || row.type}
                  </TableCell>

                  {/* Location */}
                  <TableCell>{row.location}</TableCell>

                  {/* Coordinates */}
                  <TableCell>
                    {formatCoordinates(
                      row.default_latitude,
                      row.default_longitude
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StationTable;
